import { Handle, InternalNode, Position } from '@xyflow/react';

// returns the position (top,right,bottom or right) passed node compared to
function getParams(nodeA: InternalNode, nodeB: InternalNode) {
  const centerA = getNodeCenter(nodeA);
  const centerB = getNodeCenter(nodeB);

  const horizontalDiff = Math.abs(centerA.x - centerB.x);
  const verticalDiff = Math.abs(centerA.y - centerB.y);

  let position;

  // when the horizontal difference between the nodes is bigger, we use Position.Left or Position.Right for the handle
  if (horizontalDiff > verticalDiff) {
    position = centerA.x > centerB.x ? Position.Left : Position.Right;
  } else {
    // here the vertical difference between the nodes is bigger, so we use Position.Top or Position.Bottom for the handle
    position = centerA.y > centerB.y ? Position.Top : Position.Bottom;
  }

  const [x, y] = getHandleCoordsByPosition(nodeA, position);
  return { x, y, position };
}

function getHandleCoordsByPosition(
  node: InternalNode,
  handlePosition: Position
) {
  // all handles are from type source, that's why we use handleBounds.source here
  try {
    if (!node?.internals?.handleBounds?.source)
      throw new Error('No handleBounds.source found');
    const handle = node.internals.handleBounds.source.find(
      (h) => h.position === handlePosition
    );
    if (!handle) throw new Error('No handle found');
    let offsetX = handle.width / 2;
    let offsetY = handle.height / 2;

    // this is a tiny detail to make the markerEnd of an edge visible.
    // The handle position that gets calculated has the origin top-left, so depending which side we are using, we add a little offset
    // when the handlePosition is Position.Right for example, we need to add an offset as big as the handle itself in order to get the correct position
    switch (handlePosition) {
      case Position.Left:
        offsetX = 0;
        break;
      case Position.Right:
        offsetX = handle.width;
        break;
      case Position.Top:
        offsetY = 0;
        break;
      case Position.Bottom:
        offsetY = handle.height;
        break;
    }

    const x = node.internals.positionAbsolute.x + handle.x + offsetX;
    const y = node.internals.positionAbsolute.y + handle.y + offsetY;

    return [x, y];
  } catch (error) {
    console.error(error);
    return [0, 0];
  }
}

function getNodeCenter(node: InternalNode) {
  try {
    if (!node?.internals?.positionAbsolute)
      throw new Error('No positionAbsolute found');
    if (!node?.measured?.height || !node?.measured?.width)
      throw new Error('No measured found');
    return {
      x: node.internals.positionAbsolute.x + node.measured.width / 2,
      y: node.internals.positionAbsolute.y + node.measured.height / 2,
    };
  } catch (error) {
    console.error(error);
    return { x: 0, y: 0 };
  }
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export const getEdgeParams = (source: InternalNode, target: InternalNode) => {
  const { x: sx, y: sy, position: sourcePos } = getParams(source, target);
  const { x: tx, y: ty, position: targetPos } = getParams(target, source);

  return {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
  };
};

export const getHandleCenter = (
  node: InternalNode,
  type: 'source' | 'target',
  handleId?: string | null
) => {
  // console.log('getHandleCenter called with:', { node, type, handleId });
  const bounds = node.internals.handleBounds?.[type];
  if (!bounds || bounds.length === 0) return null;

  // stick to the handle that React Flow says we're connected to
  const handle =
    (handleId && bounds.find((h) => h.id === handleId)) ?? bounds[0];

  // console.log('Selected handle:', handle);

  const handleTyped = handle as Handle;

  const x =
    node.internals.positionAbsolute.x + handleTyped.x + handleTyped.width / 2;
  const y =
    node.internals.positionAbsolute.y + handleTyped.y + handleTyped.height / 2;

  const position: Position = handleTyped.position ?? Position.Bottom;

  return { x, y, position };
};
